// src/components/Configuraciones/Configuraciones.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FEATURE_BITS as FB } from "../../constants/permisos";
import Perfil from "./Perfil";
import Firmware from "./Firmware";
import EditarParametros from "./EditarParametros";
import Respaldo from "./Respaldo";
import { usePermisos } from "../../context/PermisosContext";
import { useAuth } from "../../context/AuthContext";
import { isSuperAdmin } from "../../services/authUtils";

// ===== helper: leer bit de endpoint (1-based) =====
const hasFromBitset = (featureKey) => {
  try {
    const bitset = localStorage.getItem("endpoint") || "";
    const pos = FB[featureKey];
    if (typeof pos !== "number") return undefined; // si el bit no existe, no bloqueamos
    const i = pos - 1;
    return i >= 0 && i < bitset.length && bitset[i] === "1";
  } catch {
    return undefined;
  }
};

// Tabs gobernadas por bitset (las de super se inyectan aparte)
const TAB_TO_FEATURE = {
  perfil: "CONFIGURACIONES_TAB_PERFIL_VIEW",
  parametros: "CONFIGURACIONES_TAB_PARAMETROS_VIEW",
  firmware: "CONFIGURACIONES_TAB_FIRMWARE_VIEW",
};

const BASE_TABS = [
  { id: "perfil", label: "Mi cuenta" },
  { id: "parametros", label: "Rangos y alertas" },
  { id: "firmware", label: "Conexión y equipos" },
];

const Configuraciones = ({ initialTab = "perfil" }) => {
  const { user } = useAuth();
  const { can } = usePermisos();
  const [searchParams, setSearchParams] = useSearchParams();

  const esSuper = useMemo(
    () =>
      !!isSuperAdmin() ||
      user?.superUsuario === true ||
      String(user?.rol || "").toLowerCase() === "superadmin" ||
      localStorage.getItem("esSuperAdmin") === "true",
    [user?.superUsuario, user?.rol]
  );

  const canViewConfig = esSuper || can("CONFIGURACIONES_VIEW");

  // Tabs visibles por bitset + inyecciones de super
  const visibleTabs = useMemo(() => {
    const tabs = BASE_TABS.filter((t) => {
      const key = TAB_TO_FEATURE[t.id];
      const allowed = key ? (esSuper || can(key)) : true;
      return allowed === undefined ? true : allowed === true;
    });

    if (esSuper) {
      // Agrega "Respaldo" al final
      tabs.push({ id: "respaldo", label: "Administración del sistema" });
    }

    return tabs;
  }, [esSuper, can]);

  // Lee sub desde URL y selecciona una válida
  const subFromUrl = searchParams.get("sub") || "";
  const pickValid = (candidate) => {
    const fallback = visibleTabs[0]?.id || null;
    if (!candidate) return fallback;
    return visibleTabs.some((t) => t.id === candidate) ? candidate : fallback;
  };

  const initial = pickValid(subFromUrl || initialTab);
  const [tab, setTab] = useState(initial);
  const [parametrosEmbebido, setParametrosEmbebido] = useState(false);

  // Revalida si cambian permisos/URL/prop
  useEffect(() => {
    const next = pickValid(subFromUrl || initialTab || tab);
    if (next !== tab) {
      setTab(next);
      setParametrosEmbebido(false); // reset si cambiamos de tab
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, subFromUrl, initialTab]);

  // Espeja estado → URL (sin loops)
  useEffect(() => {
    if (!tab) return;
    const next = new URLSearchParams(searchParams);
    if (next.get("sub") !== tab) {
      next.set("sub", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Guardia extra: si alguien intenta ir a tabs de super sin serlo
  useEffect(() => {
    if (!esSuper && tab === "respaldo") {
      const first = visibleTabs[0]?.id ?? null;
      setTab(first);
      setParametrosEmbebido(false);
    }
  }, [tab, esSuper, visibleTabs]);

  if (!canViewConfig) {
    return (
      <div className="bg-neutral-900 text-white p-2 sm:p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-6">CONFIGURACIONES</h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">No tienes permiso para ver esta sección.</p>
        </div>
      </div>
    );
  }

  if (!tab) {
    return (
      <div className="bg-neutral-900 text-white p-2 sm:p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-6">CONFIGURACIONES</h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">No hay pestañas disponibles con tus permisos actuales.</p>
        </div>
      </div>
    );
  }

  const renderContenido = () => {
    switch (tab) {
      case "perfil":
        return <Perfil />;

      case "parametros": {
        if (hasFromBitset("CONFIGURACIONES_TAB_PARAMETROS_VIEW") === false && !esSuper) return null;
        if (!parametrosEmbebido) {
          return (
            <div className="space-y-3">
              <p className="text-gray-300">
                Administra los <b>Valores de Referencia</b> por tipo de parámetro (pH, T°, etc.).
              </p>
              <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-400">Personaliza los rangos de tu cuenta o vuelve a los predeterminados.</p>
                </div>
                <button
                  onClick={() => setParametrosEmbebido(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                >
                  Abrir editor
                </button>
              </div>
            </div>
          );
        }
        return <EditarParametros onBack={() => setParametrosEmbebido(false)} />;
      }

      case "firmware": {
        const canViewFirmware = hasFromBitset("CONFIGURACIONES_TAB_FIRMWARE_VIEW") !== false;
        if (!canViewFirmware) {
          return (
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
              <p className="text-sm text-yellow-400">No tienes permiso para ver Firmware.</p>
            </div>
          );
        }
        return <Firmware initialTab="flashear" />; // Firmware internamente maneja su propio ?sub si lo quieres
      }

      case "respaldo": {
        if (!esSuper) return null; // doble seguro
        return <Respaldo />;
      }

      default:
        return null;
    }
  };

  return (
    <div className="bg-neutral-900 text-white p-4 sm:p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
      <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
        CONFIGURACIONES
      </h1>

      {/* Tabs visibles */}
      <div className="mb-4 sm:mb-6 flex justify-center">
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTab(t.id);
                if (t.id !== "parametros") setParametrosEmbebido(false);
              }}
              className={`w-full sm:w-auto px-3 sm:px-4 py-2 rounded text-sm sm:text-base transition-all duration-200 ${
                tab === t.id ? "bg-green-500 text-black" : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 sm:mt-6">{renderContenido()}</div>
    </div>
  );
};

export default Configuraciones;
